import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Table } from "../../../components/SystemComponent/table";
import { GroupButton } from "../../../components/SystemComponent/GroupButton";
import { Badge } from "../../../components/SystemComponent/Bagde";
import { Modal } from "../../../components/SystemComponent/Modal";
import { Button } from "../../../components/SystemComponent/Button";
import { MdDelete, MdHistory } from "react-icons/md";
import { createApi } from "../../../../core/api";

export const CompanyPackageItemUsageLogCrud = () => {
  const api = useMemo(() => createApi(), []);
  const { setPageMenus } = useOutletContext<any>();

  const [data, setData] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, c, i] = await Promise.all([
        api.companyPackageItemUsageLog.getAll(),
        api.company.getAll(),
        api.packageItem.getAll(),
      ]);

      if (l?.ok) setData(l.data?.data ?? l.data ?? []);
      if (c?.ok) setCompanies(c.data?.data ?? c.data ?? []);
      if (i?.ok) setItems(i.data?.data ?? i.data ?? []);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    setPageMenus([
      {
        label: "Company Package Item Usage Logs",
        items: [
          {
            label: "Company Package Item Usage Logs",
            route: "/dashboard/company-package-item-usage-log",
          },
        ],
      },
    ]);
    load();
    return () => setPageMenus([]);
  }, [load, setPageMenus]);

  const confirmDelete = (id: number) => {
    setDeleteId(id);
    setDeleteError(null);
  };

  const doDelete = async () => {
    if (!deleteId || deleting) return;

    setDeleting(true);
    setDeleteError(null);

    try {
      const r = await api.companyPackageItemUsageLog.delete(deleteId);
      if (!r?.ok) throw new Error(r?.message || "Delete failed");

      setDeleteId(null);
      load();
    } catch (e: any) {
      setDeleteError(e?.message || "Unexpected error occurred");
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo<any[]>(
    () => [
      {
        key: "id",
        title: "ID",
        minWidth: 80,
        accessor: (r: any) => r.id,
      },
      {
        key: "company",
        title: "Company",
        minWidth: 260,
        accessor: (r: any) =>
          companies.find((x) => x.id === r.companyId)?.name ?? "",
        render: (_: any, v: string) => (
          <Badge label={v} color="#0EA5E9" bg="rgba(14,165,233,.15)" />
        ),
      },
      {
        key: "item",
        title: "Package Item",
        minWidth: 260,
        accessor: (r: any) =>
          items.find((x) => x.id === r.packageItemId)?.name ?? "",
        render: (_: any, v: string) => (
          <Badge
            icon={MdHistory}
            label={v}
            color="#F59E0B"
            bg="rgba(245,158,11,.15)"
          />
        ),
      },
      {
        key: "amount",
        title: "Amount",
        minWidth: 120,
        accessor: (r: any) => r.amount,
      },
      {
        key: "createdDate",
        title: "Created Date",
        minWidth: 200,
        accessor: (r: any) => r.createdDate,
      },
      {
        key: "actions",
        title: "Actions",
        minWidth: 140,
        render: (row: any) => (
          <GroupButton
            items={[
              {
                id: "delete",
                icon: MdDelete,
                color: "#DC2626",
                onClick: () => confirmDelete(row.id),
              },
            ]}
          />
        ),
      },
    ],
    [companies, items]
  );

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div>
          <div style={S.title}>Company Package Item Usage Logs</div>
          <div style={S.subTitle}>Track package item usage history</div>
        </div>
      </div>

      <Table
        tableKey="company-package-item-usage-log"
        loading={loading}
        columns={columns}
        data={data}
        features={{
          sortable: true,
          filterable: true,
          pagination: true,
          persistence: true,
        }}
      />

      <Modal
        open={!!deleteId}
        onClose={() => (!deleting ? setDeleteId(null) : null)}
      >
        <div style={S.modalTitle}>Delete Usage Log</div>

        <div style={{ fontSize: 14, color: "#334155" }}>
          Are you sure you want to delete this usage log?
        </div>

        {deleteError && <div style={S.error}>{deleteError}</div>}

        <div style={S.actions}>
          <Button
            variant="ghost"
            onClick={() => setDeleteId(null)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={doDelete}
            loading={deleting}
            disabled={deleting}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
};

const S: Record<string, React.CSSProperties> = {
  wrap: {
    position: "absolute",
    inset: 0,
    padding: 20,
    background: "#F8FAFC",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  title: {
    fontSize: 22,
    fontWeight: 800,
    color: "#0F172A",
  },
  subTitle: {
    fontSize: 13,
    color: "#64748B",
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 800,
    marginBottom: 14,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 20,
  },
  error: {
    marginTop: 8,
    fontSize: 12,
    fontWeight: 600,
    color: "#DC2626",
  },
};
